import {
  CREDENTIAL_DOCUMENT_UPLOAD_MAX_FILE_NAME_LENGTH,
  CREDENTIAL_DOCUMENT_UPLOAD_MAX_SIZE_BYTES,
  CREDENTIAL_DOCUMENT_UPLOAD_MIME_TYPES,
} from "@api/shared-runtime/credential-document-upload";

const BYTES_PER_MEGABYTE = 1024 * 1024;
const BYTES_PER_KILOBYTE = 1024;
export const REVIEW_UPLOAD_MAX_SIZE_MB =
  CREDENTIAL_DOCUMENT_UPLOAD_MAX_SIZE_BYTES / BYTES_PER_MEGABYTE;

type UploadMimeType = (typeof CREDENTIAL_DOCUMENT_UPLOAD_MIME_TYPES)[number];
const MIME_TYPE_BY_EXTENSION: Record<string, UploadMimeType> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
  heif: "image/heif",
  webp: "image/webp",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

/** Some browsers omit MIME types for HEIC and Word files; the server still
 * verifies the uploaded bytes before making the evidence available. */
export function validateEvidenceUpload(files: File[]) {
  if (files.length !== 1) {
    throw new Error("Choose one file at a time.");
  }
  const file = files[0];
  if (file.size === 0) {
    throw new Error("This file is empty. Choose another file.");
  }
  if (file.size > CREDENTIAL_DOCUMENT_UPLOAD_MAX_SIZE_BYTES) {
    throw new Error(
      `Choose a file no larger than ${REVIEW_UPLOAD_MAX_SIZE_MB} MB.`,
    );
  }
  if (file.name.length > CREDENTIAL_DOCUMENT_UPLOAD_MAX_FILE_NAME_LENGTH) {
    throw new Error("This filename is too long. Shorten it and try again.");
  }
  const declaredMime =
    file.type ||
    MIME_TYPE_BY_EXTENSION[file.name.split(".").at(-1)?.toLowerCase() ?? ""];
  const mimeType = CREDENTIAL_DOCUMENT_UPLOAD_MIME_TYPES.find(
    (allowed) => allowed === declaredMime,
  );
  if (!mimeType) {
    throw new Error(
      "Choose a PDF, JPG, PNG, HEIC, HEIF, WebP, or Word (.docx) file.",
    );
  }
  return { file, mimeType };
}

export function evidenceFileSize(bytes: number) {
  if (bytes >= BYTES_PER_MEGABYTE) {
    return `${(bytes / BYTES_PER_MEGABYTE).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / BYTES_PER_KILOBYTE))} KB`;
}
